import { useState } from "react";
import { toast } from "sonner";
import {
  Phone, Mail, Linkedin, Copy, RefreshCw, Send, Info, Building2,
  TrendingUp, AlertTriangle, ShieldCheck, User, Zap,
} from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";


const _daysSince = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};


function Badge({ tone = "slate", icon: Icon, children }) {
  const tones = {
    emerald: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    amber: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    rose: "text-rose-300 border-rose-500/40 bg-rose-500/10",
    slate: "text-slate-400 border-slate-700 bg-slate-800/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm border ${tones[tone] || tones.slate}`}>
      {Icon && <Icon className="h-3 w-3" />} {children}
    </span>
  );
}


function CopyBtn({ value, label = "Copy" }) {
  if (!value) return null;
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); }}
      className="text-slate-500 hover:text-emerald-300"
      title={label}
    >
      <Copy className="h-3 w-3" />
    </button>
  );
}


export default function BusinessOwnerIntelligencePanel({ opp, onUpdate }) {
  const [busy, setBusy] = useState(false);
  if (!opp) return null;
  if ((opp.asset_type || "").toLowerCase() !== "business") return null;

  const status = opp.l411_enrichment_status || "Not Run";

  const runEnrich = async (force = false) => {
    if (force && !window.confirm("This will consume 1 Lead411 credit. Continue?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/opportunities/${opp.oid}/lead411/enrich${force ? "?force=true" : ""}`);
      const res = r.data?.result || {};
      if (res.status === "success") toast.success("Business owner enriched via Lead411");
      else if (res.status === "cached") toast.info("Cache still fresh");
      else if (res.status === "no_match") toast.warning("No Lead411 match ,  task created for manual research");
      else if (res.status === "blocked") toast.error(`Blocked: ${res.reason}`);
      onUpdate?.(r.data.opp);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === "object" && d?.reason) ? `Blocked: ${d.reason}` : (d || "Enrichment failed");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // Not Run state
  if (status === "Not Run") {
    return (
      <div className="ae-card p-5 space-y-3" data-testid="business-owner-panel">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
          <span>Business Owner Intelligence</span>
          <span title="Platform default: PDL + Hunter (free tier). Lead411 activates automatically when its key is added. Subscribers can BYOK in Settings." className="cursor-help text-slate-600"><Info className="h-3 w-3" /></span>
        </div>
        <div className="text-sm text-slate-500">Business owner contact not retrieved.</div>
        <button
          onClick={() => runEnrich(false)} disabled={busy}
          data-testid="business-owner-get-btn"
          className="text-xs inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
        >
          <User className={`h-3.5 w-3.5 ${busy ? "animate-pulse" : ""}`} />
          {busy ? "Retrieving…" : "Get Business Owner Contact →"}
        </button>
      </div>
    );
  }

  if (status === "No Match") {
    return (
      <div className="ae-card p-5 space-y-3" data-testid="business-owner-panel">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Business Owner Intelligence</div>
        <div className="text-sm text-slate-400">No match from any enrichment source. Try manual search or direct mail.</div>
        <button
          onClick={() => runEnrich(true)} disabled={busy}
          className="text-xs inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-white/10 hover:border-emerald-500/40 text-slate-300"
        >
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
          Retry
        </button>
      </div>
    );
  }

  const intent = opp.l411_intent_score;
  const intentTone = intent > 70 ? "emerald" : intent >= 40 ? "amber" : intent != null ? "rose" : "slate";
  const founded = opp.l411_company_founded_year;
  const yearsInBiz = founded ? (new Date().getFullYear() - founded) : null;
  const daysOld = _daysSince(opp.l411_last_enriched);
  const topics = (opp.l411_intent_topics || "").split(",").map(t => t.trim()).filter(Boolean).slice(0, 5);

  // Source attribution badge (PDL / Lead411 / Apollo / Hunter). "Manual" → no badge.
  const cds = (opp.contact_data_source || "").toLowerCase();
  const sourceBadge =
    cds.includes("lead411") ? { label: "Powered by Lead411", cls: "border-slate-700 text-slate-300 bg-slate-800/40" } :
    cds === "pdl"          ? { label: "Powered by People Data Labs", cls: "border-slate-700 text-slate-300 bg-slate-800/40" } :
    cds === "apollo"       ? { label: "Powered by Apollo", cls: "border-slate-700 text-slate-300 bg-slate-800/40" } :
    cds === "hunter"       ? { label: "Powered by Hunter.io", cls: "border-slate-700 text-slate-300 bg-slate-800/40" } :
                             null;

  return (
    <div className="ae-card p-5 space-y-5" data-testid="business-owner-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
          <span>Business Owner Intelligence</span>
          <span title="Platform default: PDL + Hunter (free tier). Lead411 activates automatically when its key is added. Subscribers can BYOK in Settings." className="cursor-help text-slate-600"><Info className="h-3 w-3" /></span>
        </div>
        {sourceBadge && (
          <span
            data-testid="business-owner-source-badge"
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${sourceBadge.cls}`}
          >
            {sourceBadge.label}
          </span>
        )}
      </div>

      {/* Row 1 ,  Company & Owner Identity */}
      <div className="space-y-1" data-testid="business-owner-identity">
        <div className="text-base font-semibold text-slate-100">
          {opp.l411_owner_full_name || "Unknown Owner"}
        </div>
        {opp.l411_owner_title && (
          <div className="text-xs text-emerald-300">{opp.l411_owner_title}</div>
        )}
        <div className="text-xs text-slate-400 flex flex-wrap gap-x-3 gap-y-1 pt-1">
          <span><Building2 className="inline h-3 w-3 mr-1" />{opp.business_name || opp.title || ", "}</span>
          {opp.l411_company_industry && <span>Industry: <span className="text-slate-200">{opp.l411_company_industry}</span></span>}
          {opp.l411_company_employee_count && <span>{opp.l411_company_employee_count} employees</span>}
          {opp.l411_company_revenue_range && <span>Revenue: {opp.l411_company_revenue_range}</span>}
          {yearsInBiz != null && <span>{yearsInBiz} years in business</span>}
        </div>
      </div>

      {/* Row 2 ,  Contact cards */}
      <div className="grid md:grid-cols-3 gap-3">
        <div className="ae-card p-4 space-y-2" data-testid="l411-phone-card">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Phone className="h-3 w-3" /> Direct Dial
          </div>
          {opp.l411_owner_phone_direct ? (
            <>
              <div className="flex items-center gap-2">
                <div className="font-mono text-sm text-slate-100">{opp.l411_owner_phone_direct}</div>
                <CopyBtn value={opp.l411_owner_phone_direct} label="Phone" />
              </div>
              <Badge tone="emerald">Direct Dial</Badge>
            </>
          ) : cds === "pdl" ? (
            <div className="text-xs text-slate-400 leading-relaxed" data-testid="l411-phone-pdl-hint">
              Phone not available from free tier.{" "}
              <a href="/app/settings#b2b-enrichment" className="text-emerald-300 hover:text-emerald-200 underline">
                Connect your own API key in Settings
              </a>{" "}
              for phone enrichment.
            </div>
          ) : (
            <div className="text-xs text-slate-500">Not found</div>
          )}
        </div>

        <div className="ae-card p-4 space-y-2" data-testid="l411-email-card">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Mail className="h-3 w-3" /> Business Email
          </div>
          {opp.l411_owner_email ? (
            <>
              <div className="flex items-center gap-2">
                <div className="text-sm text-slate-100 truncate">{opp.l411_owner_email}</div>
                <CopyBtn value={opp.l411_owner_email} label="Email" />
              </div>
              <Badge tone={opp.l411_owner_email_verified ? "emerald" : "amber"} icon={opp.l411_owner_email_verified ? ShieldCheck : AlertTriangle}>
                {opp.l411_owner_email_verified ? "Verified" : "Unverified"}
              </Badge>
            </>
          ) : (
            <div className="text-xs text-slate-500">Not found</div>
          )}
        </div>

        <div className="ae-card p-4 space-y-2" data-testid="l411-linkedin-card">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Linkedin className="h-3 w-3" /> LinkedIn
          </div>
          {opp.l411_owner_linkedin_url ? (
            <a href={opp.l411_owner_linkedin_url} target="_blank" rel="noopener noreferrer"
               className="text-sm text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1 truncate">
              <Linkedin className="h-4 w-4" /> View profile
            </a>
          ) : (
            <div className="text-xs text-slate-500">Not found</div>
          )}
        </div>
      </div>

      {/* Row 3 ,  Intelligence signals */}
      <div className="ae-card p-4 space-y-3" data-testid="l411-intel">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
          <Zap className="h-3 w-3" /> Intelligence Signals
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Intent Score (Bombora)</div>
            <div className="flex items-center gap-2 mt-1">
              <div className="font-mono text-lg text-slate-100 tabular-nums">
                {intent != null ? intent : ", "}
              </div>
              {intent != null && (
                <Badge tone={intentTone}>
                  {intent > 70 ? "High" : intent >= 40 ? "Medium" : "Low"}
                </Badge>
              )}
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Intent Topics</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {topics.length ? topics.map((t, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-sm border border-emerald-500/30 bg-emerald-500/5 text-emerald-300">{t}</span>
              )) : <span className="text-xs text-slate-500">None detected</span>}
            </div>
          </div>
        </div>
        {opp.l411_sales_trigger && (
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 flex items-start gap-2">
            <TrendingUp className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
            <div className="text-xs">
              <div className="text-amber-300 font-semibold">Recent trigger</div>
              <div className="text-amber-200/80 mt-0.5">{opp.l411_sales_trigger}</div>
            </div>
          </div>
        )}
      </div>

      {/* Row 4 ,  Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          disabled={!!opp.outreach_blocked}
          data-testid="business-outreach-btn"
          title={opp.outreach_blocked ? "Outreach blocked" : "Start outreach sequence"}
          className={`text-xs inline-flex items-center gap-2 px-3 py-2 rounded-sm border ${
            opp.outreach_blocked
              ? "border-rose-500/30 text-rose-400 cursor-not-allowed opacity-70"
              : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
          }`}
        >
          <Send className="h-3.5 w-3.5" /> Start Outreach
        </button>
        <button
          onClick={() => runEnrich(true)} disabled={busy}
          data-testid="business-refresh-btn"
          className="text-xs inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-white/10 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-300 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
          Refresh Contact
        </button>
      </div>

      {opp.l411_last_enriched && (
        <div className="text-[10px] text-slate-600 pt-2 border-t border-white/8">
          Business intel as of {fmtDateTime(opp.l411_last_enriched)}
          {daysOld != null && ` (${daysOld}d ago)`}
        </div>
      )}
    </div>
  );
}
